import React, { useState, useEffect, useMemo } from "react";
import Content from "../../components/Content";
import Layout from "../../containers/LayoutAdministrativo";
import SolicitudesService from "../../services/SolicitudesService";
import SucursalesService from "../../services/SucursalesService";
import ReembolsoService from "../../services/ReembolsoService";
import CajaService from "../../services/CajaService";
import TelefonosService from "../../services/TelefonosService";
import EstudiosService from "../../services/EstudiosService";

import logoLaboratorio from "../../img/logo-laboratorio.jpg";
import Icon from "@mdi/react";
import { mdiFilePdfBox, mdiFileExcel } from "@mdi/js";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { ejecutarConCargandoPDF } from "../../utils/pdfLoadingUtils";
import RefreshIcon from "@mui/icons-material/Refresh";

import {
  Grid,
  TextField,
  Select,
  MenuItem,
  Paper,
  Typography,
  Stack,
  Box,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  IconButton,
  Tooltip,
  FormControlLabel,
  Checkbox,
  tooltipClasses,
} from "@mui/material";
import Swal from "sweetalert2";

const ReporteDescuentos = () => {
  const getFechaHoy = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const [solicitudes, setSolicitudes] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [reembolsosList, setReembolsosList] = useState([]);
  const [estudiosMap, setEstudiosMap] = useState({});
  const [catalogEstudiosMap, setCatalogEstudiosMap] = useState({});
  const [cajaMap, setCajaMap] = useState({});
  const [loading, setLoading] = useState(true);

  // Usuario actual en localStorage
  const currentU = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("usuario")) || {};
    } catch (e) {
      return {};
    }
  }, []);

  // Filtros
  const [sucursalSeleccionada, setSucursalSeleccionada] = useState("todas");
  const [filtroDescuento, setFiltroDescuento] = useState("todos"); // "todos" | "100"
  const [fechaDe, setFechaDe] = useState(getFechaHoy());
  const [fechaA, setFechaA] = useState(getFechaHoy());

  // Checkbox Adicional
  const [mostrarDetalleEstudios, setMostrarDetalleEstudios] = useState(false);

  useEffect(() => {
    document.title = "Reporte de Descuentos";
    fetchDatosIniciales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const parseFechaDate = (fechaRaw) => {
    if (!fechaRaw) return null;
    if (fechaRaw instanceof Date) {
      return isNaN(fechaRaw.getTime())
        ? null
        : new Date(fechaRaw.getFullYear(), fechaRaw.getMonth(), fechaRaw.getDate());
    }
    const str = String(fechaRaw).trim();
    if (!str) return null;

    const datePart = str.split(" ")[0].split("T")[0];

    if (datePart.includes("/")) {
      const parts = datePart.split("/");
      if (parts.length === 3) {
        const d = Number(parts[0]);
        const m = Number(parts[1]) - 1;
        const y = Number(parts[2]);
        if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
          return new Date(y, m, d);
        }
      }
    }

    if (datePart.includes("-")) {
      const parts = datePart.split("-");
      if (parts.length === 3) {
        if (parts[0].length === 4) {
          const y = Number(parts[0]);
          const m = Number(parts[1]) - 1;
          const d = Number(parts[2]);
          if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
            return new Date(y, m, d);
          }
        } else if (parts[2].length === 4) {
          const d = Number(parts[0]);
          const m = Number(parts[1]) - 1;
          const y = Number(parts[2]);
          if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
            return new Date(y, m, d);
          }
        }
      }
    }

    const dt = new Date(str);
    if (!isNaN(dt.getTime())) {
      return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
    }
    return null;
  };

  const fetchDatosIniciales = async () => {
    setLoading(true);
    try {
      const [resSol, resSuc, resCaja, resCatEst] = await Promise.all([
        SolicitudesService.getSolicitudes().catch(() => ({ data: [] })),
        SucursalesService.getSucursales().catch(() => ({ data: [] })),
        CajaService.getCaja().catch(() => ({ data: { caja: [] } })),
        EstudiosService.getEstudios().catch(() => ({ data: [] })),
      ]);

      const listaSol = Array.isArray(resSol?.data?.solicitudes)
        ? resSol.data.solicitudes
        : Array.isArray(resSol?.data)
          ? resSol.data
          : [];

      const listaSuc = Array.isArray(resSuc?.data?.sucursales)
        ? resSuc.data.sucursales
        : Array.isArray(resSuc?.data)
          ? resSuc.data
          : [];

      const listaCaja = Array.isArray(resCaja?.data?.caja)
        ? resCaja.data.caja
        : Array.isArray(resCaja?.data)
          ? resCaja.data
          : [];

      const listaCatEst = Array.isArray(resCatEst?.data?.estudios)
        ? resCatEst.data.estudios
        : Array.isArray(resCatEst?.data)
          ? resCatEst.data
          : [];

      // Mapear catálogo de estudios por idEstudio para obtener código real del estudio
      const catMap = {};
      listaCatEst.forEach((est) => {
        if (est && est.idEstudio) {
          catMap[est.idEstudio] = est;
        }
      });
      setCatalogEstudiosMap(catMap);

      // Mapear movimientos de caja por idSolicitud
      const cMap = {};
      listaCaja.forEach((c) => {
        if (c && c.idSolicitud) {
          cMap[c.idSolicitud] = c;
        }
      });
      setCajaMap(cMap);

      // Unir datos de caja a las solicitudes
      const solicitudesConCaja = listaSol.map((s) => {
        const c = cajaMap[s.idSolicitud];
        const totalSol = Number(s.totalPagar ?? s.total ?? s.monto ?? 0);
        const descSol = Number(s.descuentoTotal ?? s.descuento ?? s.descuentoMonto ?? 0);

        let adeudoVal = 0;
        let pagadoVal = 0;

        if (c) {
          adeudoVal = Number(c.adeudo ?? 0);
          pagadoVal = Number(c.pagado ?? (totalSol - descSol - adeudoVal > 0 ? totalSol - descSol - adeudoVal : 0));
        } else if (s.adeudo !== undefined && s.adeudo !== null) {
          adeudoVal = Number(s.adeudo);
          pagadoVal = Number(s.pagado ?? (totalSol - descSol - adeudoVal > 0 ? totalSol - descSol - adeudoVal : 0));
        } else if (s.saldoPendiente !== undefined && s.saldoPendiente !== null) {
          adeudoVal = Number(s.saldoPendiente);
          pagadoVal = Number(s.pagado ?? (totalSol - descSol - adeudoVal > 0 ? totalSol - descSol - adeudoVal : 0));
        } else {
          pagadoVal = Number(s.pagado ?? 0);
          adeudoVal = Math.max(0, totalSol - descSol - pagadoVal);
        }

        return {
          ...s,
          cajaData: c,
          adeudo: adeudoVal,
          pagado: pagadoVal,
          totalPagar: totalSol,
          descuentoTotal: descSol,
        };
      });

      // Reembolsos de BD
      const rawReembolsos = [];
      try {
        const resGeneral = await ReembolsoService.getReembolsos();
        const payload = resGeneral?.data?.reembolsos ?? resGeneral?.data;
        if (Array.isArray(payload)) rawReembolsos.push(...payload);
      } catch (e) { }

      if (listaSuc.length > 0) {
        try {
          const reemProms = listaSuc.map((suc) =>
            ReembolsoService.getReembolsoActual(suc.idSucursal).catch(() => null)
          );
          const reemResps = await Promise.all(reemProms);

          reemResps.forEach((res, idx) => {
            const sucId = listaSuc[idx]?.idSucursal;
            const payload = res?.data?.reembolsos ?? res?.data;
            const arr = Array.isArray(payload) ? payload : payload ? [payload] : [];
            arr.forEach((r) => {
              if (r && typeof r === "object") {
                rawReembolsos.push({
                  ...r,
                  idSucursal: r.idSucursal ?? r.sucursalId ?? sucId,
                });
              }
            });
          });
        } catch (e) { }
      }

      try {
        const resCortes = await CajaService.getCortes().catch(() => null);
        const cortesList = Array.isArray(resCortes?.data) ? resCortes.data : Array.isArray(resCortes) ? resCortes : [];
        if (cortesList.length > 0) {
          const cortesProms = cortesList.map((c) =>
            ReembolsoService.getReembolsoPorCorte(c.idSucursal || c.sucursalId, c.idCorte || c.idCorteReembolso).catch(() => null)
          );
          const cortesResps = await Promise.all(cortesProms);
          cortesResps.forEach((res) => {
            const payload = res?.reembolsos ?? res?.data?.reembolsos ?? res;
            const arr = Array.isArray(payload) ? payload : [];
            arr.forEach((r) => {
              if (r && typeof r === "object") {
                rawReembolsos.push(r);
              }
            });
          });
        }
      } catch (e) { }

      const seenKeys = new Set();
      const listaFinalReembolsos = [];
      rawReembolsos.forEach((r) => {
        const key = r.idReembolso
          ? `id:${r.idReembolso}`
          : `${r.idSucursal || r.sucursalId}-${r.fecha}-${r.monto || r.cantidad}-${r.motivo || ""}`;
        if (!seenKeys.has(key)) {
          seenKeys.add(key);
          listaFinalReembolsos.push(r);
        }
      });

      setSolicitudes(solicitudesConCaja);
      setSucursales(listaSuc);
      setReembolsosList(listaFinalReembolsos);

      // Sucursal por defecto del usuario
      if (currentU.sucursal) {
        const foundSuc = listaSuc.find(
          (s) =>
            String(s.idSucursal) === String(currentU.sucursal) ||
            String(s.nombre).trim().toLowerCase() === String(currentU.sucursal).trim().toLowerCase()
        );
        if (foundSuc) {
          setSucursalSeleccionada(String(foundSuc.idSucursal));
        } else if (listaSuc.length > 0) {
          setSucursalSeleccionada(String(listaSuc[0].idSucursal));
        }
      } else if (listaSuc.length > 0) {
        setSucursalSeleccionada(String(listaSuc[0].idSucursal));
      }

      // Cargar estudios para cada solicitud
      const eMap = {};
      await Promise.all(
        solicitudesConCaja.map(async (sol) => {
          if (sol.idSolicitud) {
            try {
              const resEst = await SolicitudesService.getEstudiosPorSolicitud(sol.idSolicitud);
              const estudiosArr = resEst?.data?.estudios ?? resEst?.data?.solicitudesEstudios ?? resEst?.data ?? [];
              if (Array.isArray(estudiosArr)) {
                eMap[sol.idSolicitud] = estudiosArr;
              }
            } catch (e) { }
          }
        })
      );
      setEstudiosMap(eMap);
    } catch (error) {
      console.error("Error cargando datos para Reporte de Descuentos:", error);
      Swal.fire({
        title: "¡Error!",
        text: "Ocurrió un error al consultar el reporte de descuentos del servidor.",
        icon: "error",
        confirmButtonText: "Aceptar",
      });
    } finally {
      setLoading(false);
    }
  };

  const coincideSucursal = (item, sucId) => {
    if (!item || !sucId) return false;
    if (sucId === "todas") return true;

    const idSucItem = String(item.idSucursal || item.sucursalId || item.sucursal || "");
    const nomSucItem = String(item.nombreSucursal || item.sucursal || "").trim().toLowerCase();

    const targetSuc = sucursales.find(
      (s) => String(s.idSucursal) === String(sucId) || String(s.nombre).trim().toLowerCase() === String(sucId).trim().toLowerCase()
    );

    if (targetSuc) {
      const targetNom = String(targetSuc.nombre).trim().toLowerCase();
      return idSucItem === String(targetSuc.idSucursal) || nomSucItem === targetNom;
    }
    return idSucItem === String(sucId) || nomSucItem === String(sucId).trim().toLowerCase();
  };

  const getDesglosePagoSolicitud = (sol) => {
    const cajaObj = cajaMap[sol.idSolicitud] || {};

    const totalNetoOriginal = Number(cajaObj.totalPagar ?? cajaObj.total ?? sol.totalPagar ?? sol.total ?? sol.monto ?? 0);
    const descuento = Number(cajaObj.descuento ?? sol.descuentoTotal ?? sol.descuento ?? sol.descuentoMonto ?? 0);
    const iva = Number(cajaObj.iva ?? sol.iva ?? sol.montoIva ?? 0);

    // Total sin descuento = Subtotal de la caja (precio bruto de los estudios sin descuento ni IVA)
    const totalSinDescuento = Number(
      cajaObj.subtotal ??
      sol.subtotal ??
      sol.subtotalCaja ??
      sol.subtotalSolicitud ??
      (totalNetoOriginal + descuento - iva)
    );

    // Total neto = Total sin descuento - Descuento + IVA
    const totalNeto = totalSinDescuento - descuento + iva;

    const pagado = Number(cajaObj.pagado ?? sol.pagado ?? sol.aCuenta ?? (totalNetoOriginal - (cajaObj.adeudo ?? sol.adeudo ?? 0) > 0 ? totalNetoOriginal - (cajaObj.adeudo ?? sol.adeudo ?? 0) : 0));

    let adeudo = 0;
    if (cajaObj.adeudo !== undefined && cajaObj.adeudo !== null && cajaObj.adeudo !== "") {
      adeudo = Number(cajaObj.adeudo);
    } else if (sol.saldoPendiente !== undefined && sol.saldoPendiente !== null && sol.saldoPendiente !== "") {
      adeudo = Number(sol.saldoPendiente);
    } else if (sol.adeudo !== undefined && sol.adeudo !== null && sol.adeudo !== "") {
      adeudo = Number(sol.adeudo);
    } else {
      adeudo = Math.max(0, totalNeto - pagado);
    }

    let ef = 0,
      tj = 0,
      ch = 0,
      tr = 0;

    const forma = `${cajaObj.formaPago || sol.formaPago || sol.metodoPago || sol.tipoPago || ""}`.toLowerCase();

    if (forma.includes("tarjeta")) {
      tj = pagado;
    } else if (forma.includes("cheque")) {
      ch = pagado;
    } else if (forma.includes("transferencia") || forma.includes("transf")) {
      tr = pagado;
    } else {
      ef = pagado;
    }

    const reemSol = reembolsosList.filter((r) => {
      const idReemSol = String(r.idSolicitud || r.codigo || "");
      const targetId = String(sol.idSolicitud || sol.codigo || "");
      return idReemSol && idReemSol === targetId;
    });
    const montoReembolso = reemSol.reduce((acc, r) => acc + Number(r.monto || r.cantidad || 0), 0);

    const estatusPago = adeudo <= 0.001 ? "PAGADO" : "PAGO PENDIENTE";

    return {
      efectivo: ef,
      tarjeta: tj,
      cheque: ch,
      transferencia: tr,
      reembolso: montoReembolso,
      pagado: pagado,
      adeudo: adeudo,
      totalSinDescuento: totalSinDescuento,
      descuento: descuento,
      iva: iva,
      totalNeto: totalNeto,
      estatusPagoText: estatusPago,
    };
  };

  // Desglose de estudios para una solicitud (utiliza el código real del estudio del catálogo)
  const getEstudiosDesglose = (sol) => {
    const estList = estudiosMap[sol.idSolicitud] || [];
    const padres = estList.filter((e) => !e.idEstudioPadre || Number(e.idEstudioPadre) === 0);
    if (padres.length === 0) return [];

    const d = getDesglosePagoSolicitud(sol);
    const totalDescuentoSol = d.descuento;
    const sumCostosPadres = padres.reduce((acc, p) => {
      const catInfo = catalogEstudiosMap[p.idEstudio] || catalogEstudiosMap[p.id] || {};
      return acc + Number(p.costo || p.precio || catInfo.precio || catInfo.costo || 0);
    }, 0);

    return padres.map((p) => {
      const catInfo = catalogEstudiosMap[p.idEstudio] || catalogEstudiosMap[p.id] || {};
      // Código del estudio real (no el id)
      const codigoEstudio = p.codigo || p.codigoEstudio || catInfo.codigo || `EST-${p.idEstudio}`;

      const costoOriginal = Number(p.costo || p.precio || catInfo.precio || catInfo.costo || 0);
      let descEstudio = Number(p.descuento || 0);

      // Si no hay descuento individual en la solicitudEstudio pero existe un descuento global, prorratear equitativamente
      if (descEstudio <= 0 && totalDescuentoSol > 0 && sumCostosPadres > 0) {
        descEstudio = (costoOriginal / sumCostosPadres) * totalDescuentoSol;
      }

      const totalNetoEstudio = Math.max(0, costoOriginal - descEstudio);

      return {
        idEstudio: p.idEstudio,
        codigo: codigoEstudio,
        nombre: p.nombreEstudio || p.nombre || catInfo.nombre || `Estudio #${p.idEstudio}`,
        importe: costoOriginal,
        descuento: descEstudio,
        totalNeto: totalNetoEstudio,
      };
    });
  };

  // Filtrado de solicitudes con descuento
  const solicitudesFiltradas = useMemo(() => {
    const start = parseFechaDate(fechaDe <= fechaA ? fechaDe : fechaA);
    const end = parseFechaDate(fechaDe <= fechaA ? fechaA : fechaDe);
    if (!start || !end) return [];

    return solicitudes.filter((sol) => {
      // Excluir canceladas
      const estatus = `${sol.estatus ?? ""}`.trim().toUpperCase();
      if (estatus === "C" || (sol.motivoCancelacion && sol.motivoCancelacion.trim() !== "")) {
        return false;
      }

      // Validar sucursal seleccionada
      if (sucursalSeleccionada !== "todas" && !coincideSucursal(sol, sucursalSeleccionada)) {
        return false;
      }

      // Validar rango de fechas
      const dt = parseFechaDate(sol.fecha || sol.fechaCreacion || sol.created_at || sol.createdAt);
      if (!dt) return false;
      if (dt.getTime() < start.getTime() || dt.getTime() > end.getTime()) return false;

      // SOLO SOLICITUDES CON DESCUENTO
      const d = getDesglosePagoSolicitud(sol);
      if (d.descuento <= 0.001) return false;

      // Filtro Descuento (Todos vs 100%)
      if (filtroDescuento === "100") {
        const es100Porc =
          d.descuento >= d.totalSinDescuento - 0.01 ||
          (d.totalSinDescuento > 0 && d.descuento / d.totalSinDescuento >= 0.999);
        if (!es100Porc) return false;
      }

      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solicitudes, sucursalSeleccionada, filtroDescuento, fechaDe, fechaA, sucursales, reembolsosList, cajaMap]);

  // Agrupamiento por sucursal cuando se selecciona "Todas las sucursales" o sucursal individual
  const datosPorSucursal = useMemo(() => {
    if (sucursalSeleccionada === "todas") {
      const result = [];
      sucursales.forEach((suc) => {
        const solsSuc = solicitudesFiltradas.filter((sol) => coincideSucursal(sol, suc.idSucursal));
        if (solsSuc.length > 0) {
          result.push({
            sucursalObj: suc,
            nombreSucursal: (suc.nombre || "SUCURSAL").toUpperCase(),
            solicitudes: solsSuc,
          });
        }
      });
      return result;
    } else {
      const foundSuc = sucursales.find(
        (s) =>
          String(s.idSucursal) === String(sucursalSeleccionada) ||
          String(s.nombre).trim().toLowerCase() === String(sucursalSeleccionada).trim().toLowerCase()
      );
      const nom = foundSuc ? (foundSuc.nombre || "").toUpperCase() : "SUCURSAL";
      if (solicitudesFiltradas.length > 0) {
        return [
          {
            sucursalObj: foundSuc || { idSucursal: sucursalSeleccionada, nombre: nom },
            nombreSucursal: nom,
            solicitudes: solicitudesFiltradas,
          },
        ];
      }
      return [];
    }
  }, [solicitudesFiltradas, sucursalSeleccionada, sucursales]);

  // Calcular totales para un arreglo de solicitudes
  const calcularTotales = (listaSols) => {
    return listaSols.reduce(
      (acc, sol) => {
        const d = getDesglosePagoSolicitud(sol);
        return {
          efectivo: acc.efectivo + d.efectivo,
          tarjeta: acc.tarjeta + d.tarjeta,
          cheque: acc.cheque + d.cheque,
          transferencia: acc.transferencia + d.transferencia,
          reembolso: acc.reembolso + d.reembolso,
          pagado: acc.pagado + d.pagado,
          adeudo: acc.adeudo + d.adeudo,
          totalSinDescuento: acc.totalSinDescuento + d.totalSinDescuento,
          descuento: acc.descuento + d.descuento,
          iva: acc.iva + d.iva,
          totalNeto: acc.totalNeto + d.totalNeto,
        };
      },
      {
        efectivo: 0,
        tarjeta: 0,
        cheque: 0,
        transferencia: 0,
        reembolso: 0,
        pagado: 0,
        adeudo: 0,
        totalSinDescuento: 0,
        descuento: 0,
        iva: 0,
        totalNeto: 0,
      }
    );
  };

  // Restablecer filtros
  const handleRestablecer = () => {
    setFechaDe(getFechaHoy());
    setFechaA(getFechaHoy());
    setFiltroDescuento("todos");
    setMostrarDetalleEstudios(false);

    if (currentU.sucursal) {
      const foundSuc = sucursales.find(
        (s) =>
          String(s.idSucursal) === String(currentU.sucursal) ||
          String(s.nombre).trim().toLowerCase() === String(currentU.sucursal).trim().toLowerCase()
      );
      if (foundSuc) {
        setSucursalSeleccionada(String(foundSuc.idSucursal));
      } else {
        setSucursalSeleccionada("todas");
      }
    } else {
      setSucursalSeleccionada("todas");
    }
  };

  // Exportación a PDF Institucional
  const handleExportarPDF = async () => {
    if (solicitudesFiltradas.length === 0) {
      Swal.fire({
        title: "Sin datos",
        text: "No existen solicitudes con descuento en el rango de fechas seleccionado.",
        icon: "info",
        confirmButtonText: "Aceptar",
      });
      return;
    }

    await ejecutarConCargandoPDF(
      async () => {
        const doc = new jsPDF();
        const startY = 12;

    let sucursalActualObj = sucursales.find(
      (s) =>
        String(s.idSucursal) === String(sucursalSeleccionada) ||
        String(s.nombre).trim().toLowerCase() === String(sucursalSeleccionada).trim().toLowerCase()
    ) || sucursales[0] || {};

    let sucursalTelefonosStr = "";
    if (sucursalActualObj.idSucursal) {
      try {
        const resTels = await TelefonosService.getTelefonosPorSucursal(sucursalActualObj.idSucursal);
        const listaTels = Array.isArray(resTels) ? resTels : resTels?.telefonos || [];
        sucursalTelefonosStr = listaTels.map((t) => t.telefono || t).join(", ");
      } catch (e) { }
    }

    const sucursalNomHeader = sucursalSeleccionada === "todas" ? "TODAS LAS SUCURSALES" : (sucursalActualObj.nombre || "SUCURSAL").toUpperCase();
    const calleText = [sucursalActualObj.calle, sucursalActualObj.numExterior, sucursalActualObj.numInterior].filter(Boolean).join(" ");
    const colCpText = `Col. ${sucursalActualObj.colonia || ""} C.P. ${sucursalActualObj.cPostal || ""} ${sucursalActualObj.ciudad || ""}, ${sucursalActualObj.estado || ""}`;
    const telText = `Tel. ${sucursalTelefonosStr || sucursalActualObj.telefono || ""}`;

    // Dibujar Logo
    try {
      doc.addImage(logoLaboratorio, "JPEG", 14, startY, 45, 28);
    } catch (e) { }

    // Rectángulo institucional "INFORMES"
    doc.setFillColor(24, 35, 79);
    doc.rect(115, startY, 81, 7, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("INFORMES", 155.5, startY + 5, { align: "center" });

    // Datos sucursal a la derecha
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text(sucursalNomHeader, 196, startY + 12, { align: "right" });

    let currY = startY + 16;
    if (calleText.trim()) {
      doc.setFont("helvetica", "normal");
      doc.text(`Dirección: ${calleText}`, 196, currY, { align: "right" });
      currY += 4;
    }
    doc.setFont("helvetica", "normal");
    doc.text(colCpText, 196, currY, { align: "right" });
    currY += 4;
    doc.text(telText, 196, currY, { align: "right" });

    // Título
    currY += 8;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(24, 35, 79);
    doc.text(`REPORTE DE DESCUENTOS - SUCURSAL: ${sucursalNomHeader}`, 14, currY);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);
    doc.text(`Período del ${fechaDe} al ${fechaA} | Descuento: ${filtroDescuento === "100" ? "100%" : "Todos"}`, 14, currY + 4);

    let nextY = currY + 10;

    // Generar tablas por cada sucursal
    datosPorSucursal.forEach((sucGroup) => {
      const filasPdf = [];

      sucGroup.solicitudes.forEach((sol) => {
        const d = getDesglosePagoSolicitud(sol);
        const pacienteNom = `${sol.nombrePaciente || ""} ${sol.apPaternoPaciente || ""} ${sol.apMaternoPaciente || ""}`.trim();
        const tieneEmpresa = sol.nombreConvenio && sol.nombreConvenio.trim() !== "";
        const pacienteText = tieneEmpresa ? `${pacienteNom} (${sol.nombreConvenio.trim()})` : pacienteNom;

        filasPdf.push([
          sol.codigo || sol.idSolicitud,
          pacienteText,
          d.estatusPagoText,
          `$${d.efectivo.toFixed(2)}`,
          `$${d.tarjeta.toFixed(2)}`,
          `$${d.cheque.toFixed(2)}`,
          `$${d.transferencia.toFixed(2)}`,
          `$${d.reembolso.toFixed(2)}`,
          `$${d.pagado.toFixed(2)}`,
          `$${d.adeudo.toFixed(2)}`,
          `$${d.totalSinDescuento.toFixed(2)}`,
          `$${d.descuento.toFixed(2)}`,
          `$${d.iva.toFixed(2)}`,
          `$${d.totalNeto.toFixed(2)}`,
        ]);

        // Fila Motivo de Descuento
        const tieneMotivo = d.descuento > 0 || (sol.motivoDescuento && sol.motivoDescuento.trim() !== "");
        if (tieneMotivo) {
          filasPdf.push([
            {
              content: `Motivo del descuento: ${sol.motivoDescuento || "Descuento aplicado"}`,
              colSpan: 14,
              styles: { fillColor: [255, 248, 225], textColor: [183, 129, 3], fontStyle: "italic" },
            },
          ]);
        }

        // Fila Detalle de Estudios
        if (mostrarDetalleEstudios) {
          const estudiosDesglose = getEstudiosDesglose(sol);
          if (estudiosDesglose.length > 0) {
            const estudiosStr = estudiosDesglose
              .map((e) => `[${e.codigo}] ${e.nombre} (Importe: $${e.importe.toFixed(2)} | Desc: $${e.descuento.toFixed(2)} | Neto: $${e.totalNeto.toFixed(2)})`)
              .join("\n");

            filasPdf.push([
              {
                content: `Detalle de estudios:\n${estudiosStr}`,
                colSpan: 14,
                styles: { fillColor: [238, 242, 246], textColor: [24, 35, 79], fontSize: 6.5 },
              },
            ]);
          }
        }
      });

      // Totales por sucursal
      const tSuc = calcularTotales(sucGroup.solicitudes);
      filasPdf.push([
        { content: "Totales:", colSpan: 3, styles: { fontStyle: "bold", halign: "right", fillColor: [238, 242, 246] } },
        `$${tSuc.efectivo.toFixed(2)}`,
        `$${tSuc.tarjeta.toFixed(2)}`,
        `$${tSuc.cheque.toFixed(2)}`,
        `$${tSuc.transferencia.toFixed(2)}`,
        `$${tSuc.reembolso.toFixed(2)}`,
        `$${tSuc.pagado.toFixed(2)}`,
        `$${tSuc.adeudo.toFixed(2)}`,
        `$${tSuc.totalSinDescuento.toFixed(2)}`,
        `$${tSuc.descuento.toFixed(2)}`,
        `$${tSuc.iva.toFixed(2)}`,
        `$${tSuc.totalNeto.toFixed(2)}`,
      ]);

      autoTable(doc, {
        startY: nextY,
        head: [
          [
            { content: sucGroup.nombreSucursal, colSpan: 14, styles: { halign: "center", fillColor: [24, 35, 79], textColor: [255, 255, 255], fontStyle: "bold" } },
          ],
          [
            "Código",
            "Paciente",
            "Estatus Pago",
            "Pago Efec",
            "Pago Tarj",
            "Pago Cheq",
            "Pago Transf",
            "Reembolsos",
            "Total Pagado",
            "Total Por Pagar",
            "Total Sin Desc",
            "Descuento",
            "IVA",
            "Total Neto",
          ],
        ],
        body: filasPdf,
        headStyles: { fillColor: [0, 103, 135], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 7 },
        styles: { fontSize: 6.2, cellPadding: 1.5, halign: "center" },
        columnStyles: { 0: { halign: "left", fontStyle: "bold" }, 1: { halign: "left" } },
      });

      nextY = doc.lastAutoTable.finalY + 8;
    });

    // Pie de Página
    const pageCount = doc.internal.getNumberOfPages();
    const hoyImpStr = new Date().toLocaleString();

    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(`Impreso: ${hoyImpStr}`, 14, 287);
      doc.text(`Página ${i} de ${pageCount}`, 196, 287, { align: "right" });
    }

        doc.save(`Reporte_Descuentos_${fechaDe}_al_${fechaA}.pdf`);
      },
      "Generando reporte de Descuentos en PDF...",
      "Procesando documento"
    );
  };

  // Exportación a Excel (.xlsx) - SIN columna Sucursal, con Motivo de Descuento como columna extra al final
  const handleExportarExcel = () => {
    if (solicitudesFiltradas.length === 0) {
      Swal.fire({
        title: "Sin datos",
        text: "No existen solicitudes con descuento en el rango de fechas seleccionado para exportar a Excel.",
        icon: "info",
        confirmButtonText: "Aceptar",
      });
      return;
    }

    const rowsExcel = [];

    // Fila 1: Encabezados principales (SIN columna Sucursal, Motivo al final)
    rowsExcel.push([
      "Código",
      "Paciente",
      "Estatus Pago",
      "Pago Efectivo",
      "Pago Tarjeta",
      "Pago Cheque",
      "Pago Transferencia",
      "Reembolsos",
      "Total Pagado",
      "Total Por Pagar",
      "Total Sin Descuento",
      "Descuento ($)",
      "IVA ($)",
      "Total Neto",
      "Motivo del Descuento",
    ]);

    // Recorrer todas las solicitudes unificadas en una sola tabla de Excel
    solicitudesFiltradas.forEach((sol) => {
      const d = getDesglosePagoSolicitud(sol);
      const pacienteNom = `${sol.nombrePaciente || ""} ${sol.apPaternoPaciente || ""} ${sol.apMaternoPaciente || ""}`.trim();
      const tieneEmpresa = sol.nombreConvenio && sol.nombreConvenio.trim() !== "";
      const pacienteText = tieneEmpresa ? `${pacienteNom} (${sol.nombreConvenio.trim()})` : pacienteNom;

      rowsExcel.push([
        sol.codigo || sol.idSolicitud,
        pacienteText,
        d.estatusPagoText,
        d.efectivo,
        d.tarjeta,
        d.cheque,
        d.transferencia,
        d.reembolso,
        d.pagado,
        d.adeudo,
        d.totalSinDescuento,
        d.descuento,
        d.iva,
        d.totalNeto,
        sol.motivoDescuento || "Descuento en solicitud",
      ]);

      // Si está activo mostrar detalles de estudios, se añaden las filas de estudios debajo de la solicitud
      if (mostrarDetalleEstudios) {
        const estudiosDesglose = getEstudiosDesglose(sol);
        estudiosDesglose.forEach((est) => {
          rowsExcel.push([
            `Estudio: [${est.codigo}]`,
            est.nombre,
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            est.importe,
            est.descuento,
            "",
            est.totalNeto,
            "Detalle de estudio",
          ]);
        });
      }
    });

    // Fila Totales Generales
    const tGen = calcularTotales(solicitudesFiltradas);
    rowsExcel.push([
      "TOTALES ACUMULADOS",
      "",
      "",
      tGen.efectivo,
      tGen.tarjeta,
      tGen.cheque,
      tGen.transferencia,
      tGen.reembolso,
      tGen.pagado,
      tGen.adeudo,
      tGen.totalSinDescuento,
      tGen.descuento,
      tGen.iva,
      tGen.totalNeto,
      "",
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet(rowsExcel);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Descuentos");

    XLSX.writeFile(workbook, `Reporte_Descuentos_${fechaDe}_al_${fechaA}.xlsx`);
  };

  const sucursalNomActual = useMemo(() => {
    if (sucursalSeleccionada === "todas") return "TODAS LAS SUCURSALES";
    const found = sucursales.find(
      (s) =>
        String(s.idSucursal) === String(sucursalSeleccionada) ||
        String(s.nombre).trim().toLowerCase() === String(sucursalSeleccionada).trim().toLowerCase()
    );
    return found ? (found.nombre || "").toUpperCase() : "SUCURSAL";
  }, [sucursales, sucursalSeleccionada]);

  return (
    <Layout>
      <Content>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            minHeight: "80vh",
            py: 3,
          }}
        >
          <Stack spacing={3} sx={{ width: "100%", pl: "3vw", pr: "10vw", boxSizing: "border-box" }}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                borderRadius: 4,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
              className="paperSol"
            >
              {/* Título Principal */}
              <Box sx={{ width: "100%", display: "flex", justifyContent: "center", mb: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    background: "linear-gradient(90deg, #006787, #014e65)",
                    color: "white",
                    padding: "0.5vw 2vw",
                    borderRadius: "8px",
                    textAlign: "center",
                    fontWeight: "bold",
                  }}
                >
                  REPORTE DE DESCUENTOS
                </Typography>
              </Box>

              {/* Filtros */}
              <Box sx={{ width: "95%", maxWidth: "1200px", mb: 4 }}>
                <Table
                  sx={{
                    width: "100%",
                    backgroundColor: "white",
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    overflow: "hidden",
                    "& .MuiTableCell-root": { padding: "10px 14px" },
                  }}
                >
                  <TableHead sx={{ backgroundColor: "#18234f" }}>
                    <TableRow>
                      <TableCell colSpan={2} align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "1.05rem" }}>
                        Filtros del Reporte
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Fila Sucursal */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", width: "15%", backgroundColor: "#f8f9fa" }}>
                        Sucursal:
                      </TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          value={sucursalSeleccionada}
                          onChange={(e) => setSucursalSeleccionada(e.target.value)}
                          sx={{ width: "320px" }}
                        >
                          <MenuItem value="todas">Todas las sucursales</MenuItem>
                          {sucursales.map((suc) => (
                            <MenuItem key={suc.idSucursal} value={String(suc.idSucursal)}>
                              {suc.nombre}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                    </TableRow>

                    {/* Fila Descuento */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>
                        Descuento:
                      </TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          value={filtroDescuento}
                          onChange={(e) => setFiltroDescuento(e.target.value)}
                          sx={{ width: "220px" }}
                        >
                          <MenuItem value="todos">Todos</MenuItem>
                          <MenuItem value="100">100%</MenuItem>
                        </Select>
                      </TableCell>
                    </TableRow>

                    {/* Fila Adicionales Checkbox */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>
                        Adicionales:
                      </TableCell>
                      <TableCell>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={mostrarDetalleEstudios}
                              onChange={(e) => setMostrarDetalleEstudios(e.target.checked)}
                              size="small"
                              sx={{ color: "#006787", "&.Mui-checked": { color: "#006787" } }}
                            />
                          }
                          label={<Typography variant="body2">Mostrar detalles de estudios</Typography>}
                        />
                      </TableCell>
                    </TableRow>

                    {/* Fila Rango de Fechas */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>
                        Rango de fechas:
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={3} alignItems="center">
                          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                            De:
                          </Typography>
                          <TextField
                            type="date"
                            size="small"
                            value={fechaDe}
                            onChange={(e) => setFechaDe(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: "180px" }}
                          />
                          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                            A:
                          </Typography>
                          <TextField
                            type="date"
                            size="small"
                            value={fechaA}
                            onChange={(e) => setFechaA(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: "180px" }}
                          />

                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleRestablecer}
                            startIcon={<RefreshIcon />}
                            sx={{ backgroundColor: "#18234f", color: "white", ml: 2 }}
                          >
                            Restablecer
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>

              {/* Tablas de Resultados (Separadas por Sucursal si se seleccionó "Todas") */}
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress sx={{ color: "#006787" }} />
                </Box>
              ) : datosPorSucursal.length === 0 ? (
                <Box
                  sx={{
                    width: "90%",
                    maxWidth: "1000px",
                    textAlign: "center",
                    py: 4,
                    mb: 3,
                    backgroundColor: "#fff8e1",
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="subtitle1" sx={{ color: "#b78103", fontWeight: "bold" }}>
                    No existen solicitudes con descuento para los filtros seleccionados ({fechaDe} al {fechaA}).
                  </Typography>
                </Box>
              ) : (
                datosPorSucursal.map((sucGroup) => {
                  const tSuc = calcularTotales(sucGroup.solicitudes);

                  return (
                    <Box key={sucGroup.nombreSucursal} sx={{ width: "100%", maxWidth: "1200px", mb: 5 }}>
                      <Table
                        sx={{
                          width: "50%",
                          backgroundColor: "white",
                          border: "1px solid #e0e0e0",
                          borderRadius: "8px",
                          overflow: "hidden",
                          "& .MuiTableCell-root": { padding: "8px 10px", fontSize: "0.82rem" },
                        }}
                      >
                        <TableHead>
                          {/* Fila 1: Nombre de Sucursal */}
                          <TableRow sx={{ backgroundColor: "#18234f" }}>
                            <TableCell
                              colSpan={14}
                              align="center"
                              sx={{ color: "white", fontWeight: "bold", fontSize: "1.05rem" }}
                            >
                              {sucGroup.nombreSucursal}
                            </TableCell>
                          </TableRow>

                          {/* Fila 2: Cabeceras */}
                          <TableRow sx={{ backgroundColor: "#006787" }}>
                            <TableCell sx={{ color: "white", fontWeight: "bold" }}>Código</TableCell>
                            <TableCell sx={{ color: "white", fontWeight: "bold" }}>Paciente</TableCell>
                            <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Estatus pago</TableCell>
                            <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Pago efectivo</TableCell>
                            <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Pago tarjeta</TableCell>
                            <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Pago cheque</TableCell>
                            <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Pago transferencia</TableCell>
                            <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Reembolsos</TableCell>
                            <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Total pagado</TableCell>
                            <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Total por pagar</TableCell>
                            <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Total sin descuento</TableCell>
                            <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Descuento</TableCell>
                            <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>IVA</TableCell>
                            <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Total neto</TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {sucGroup.solicitudes.map((sol) => {
                            const d = getDesglosePagoSolicitud(sol);
                            const pacienteNom = `${sol.nombrePaciente || ""} ${sol.apPaternoPaciente || ""} ${sol.apMaternoPaciente || ""}`.trim();
                            const tieneEmpresa = sol.nombreConvenio && sol.nombreConvenio.trim() !== "";
                            const pacienteText = tieneEmpresa ? `${pacienteNom} (${sol.nombreConvenio.trim()})` : pacienteNom;

                            const tieneMotivo = d.descuento > 0 || (sol.motivoDescuento && sol.motivoDescuento.trim() !== "");
                            const estudiosDesglose = getEstudiosDesglose(sol);

                            return (
                              <React.Fragment key={sol.idSolicitud || sol.codigo}>
                                <TableRow sx={{ "&:hover": { backgroundColor: "#f5f5f5" } }}>
                                  <TableCell sx={{ fontWeight: "bold" }}>{sol.codigo || sol.idSolicitud}</TableCell>
                                  <TableCell>{pacienteText}</TableCell>
                                  <TableCell align="center">
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        px: 1,
                                        py: 0.3,
                                        borderRadius: "4px",
                                        fontWeight: "bold",
                                        backgroundColor: d.adeudo <= 0.001 ? "#e8f5e9" : "#ffebee",
                                        color: d.adeudo <= 0.001 ? "#2e7d32" : "#c62828",
                                      }}
                                    >
                                      {d.estatusPagoText}
                                    </Typography>
                                  </TableCell>

                                  <TableCell align="center">${d.efectivo.toFixed(2)}</TableCell>
                                  <TableCell align="center">${d.tarjeta.toFixed(2)}</TableCell>
                                  <TableCell align="center">${d.cheque.toFixed(2)}</TableCell>
                                  <TableCell align="center">${d.transferencia.toFixed(2)}</TableCell>
                                  <TableCell align="center" sx={{ color: "#d32f2f" }}>${d.reembolso.toFixed(2)}</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: "bold", color: "#2e7d32" }}>${d.pagado.toFixed(2)}</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: "bold", color: "#d32f2f" }}>${d.adeudo.toFixed(2)}</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: "bold" }}>${d.totalSinDescuento.toFixed(2)}</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: "bold", color: "#d32f2f" }}>${d.descuento.toFixed(2)}</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: "bold", color: "#1976d2" }}>${d.iva.toFixed(2)}</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: "bold", color: "#18234f" }}>${d.totalNeto.toFixed(2)}</TableCell>
                                </TableRow>

                                {/* Fila Motivo de Descuento */}
                                {tieneMotivo && (
                                  <TableRow sx={{ backgroundColor: "#fff8e1" }}>
                                    <TableCell colSpan={14} sx={{ fontStyle: "italic", fontSize: "0.85rem", color: "#b78103", pl: 4 }}>
                                      Motivo del descuento: {sol.motivoDescuento || "Descuento en solicitud"}
                                    </TableCell>
                                  </TableRow>
                                )}

                                {/* Fila Detalle de los Estudios */}
                                {mostrarDetalleEstudios && estudiosDesglose.length > 0 && (
                                  <TableRow sx={{ backgroundColor: "#f0f4f8" }}>
                                    <TableCell colSpan={14} sx={{ pl: 4, py: 1.5 }}>
                                      <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#18234f", mb: 1 }}>
                                        Detalle de estudios realizados
                                      </Typography>
                                      <Table size="small" sx={{ width: "98%", backgroundColor: "#ffffff", border: "1px solid #c4d1db" }}>
                                        <TableHead sx={{ backgroundColor: "#e2e9ef" }}>
                                          <TableRow>
                                            <TableCell sx={{ fontWeight: "bold", fontSize: "0.75rem" }}>Código</TableCell>
                                            <TableCell sx={{ fontWeight: "bold", fontSize: "0.75rem" }}>Estudio</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: "bold", fontSize: "0.75rem" }}>Importe</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: "bold", fontSize: "0.75rem" }}>Descuento</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: "bold", fontSize: "0.75rem" }}>Total Neto</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {estudiosDesglose.map((estItem, idx) => (
                                            <TableRow key={idx}>
                                              <TableCell sx={{ fontSize: "0.75rem", fontWeight: "bold" }}>{estItem.codigo}</TableCell>
                                              <TableCell sx={{ fontSize: "0.75rem" }}>{estItem.nombre}</TableCell>
                                              <TableCell align="center" sx={{ fontSize: "0.75rem" }}>${estItem.importe.toFixed(2)}</TableCell>
                                              <TableCell align="center" sx={{ fontSize: "0.75rem", color: "#d32f2f" }}>${estItem.descuento.toFixed(2)}</TableCell>
                                              <TableCell align="center" sx={{ fontSize: "0.75rem", fontWeight: "bold", color: "#18234f" }}>${estItem.totalNeto.toFixed(2)}</TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            );
                          })}

                          {/* Fila Totales Generales por Sucursal */}
                          <TableRow sx={{ backgroundColor: "#eef2f6", borderTop: "2px solid #18234f" }}>
                            <TableCell colSpan={3} sx={{ fontWeight: "bold", textAlign: "right" }}>Totales:</TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold" }}>${tSuc.efectivo.toFixed(2)}</TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold" }}>${tSuc.tarjeta.toFixed(2)}</TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold" }}>${tSuc.cheque.toFixed(2)}</TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold" }}>${tSuc.transferencia.toFixed(2)}</TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold", color: "#d32f2f" }}>${tSuc.reembolso.toFixed(2)}</TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold", color: "#2e7d32" }}>${tSuc.pagado.toFixed(2)}</TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold", color: "#d32f2f" }}>${tSuc.adeudo.toFixed(2)}</TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold" }}>${tSuc.totalSinDescuento.toFixed(2)}</TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold", color: "#d32f2f" }}>${tSuc.descuento.toFixed(2)}</TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold", color: "#1976d2" }}>${tSuc.iva.toFixed(2)}</TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold", color: "#18234f" }}>${tSuc.totalNeto.toFixed(2)}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </Box>
                  );
                })
              )}
            </Paper>
          </Stack>
        </Box>

        {/* Dos Botones Flotantes Laterales Derechos: PDF y Excel */}
        <Box
          sx={{
            position: "fixed",
            right: "3vw",
            top: "35vh",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            zIndex: 1000,
          }}
        >
          {/* Botón PDF */}
          <Tooltip
            title="Exportar Reporte a PDF"
            arrow
            placement="left"
            slotProps={{
              popper: {
                sx: {
                  [`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.tooltip}`]: {
                    marginRight: "0px",
                  },
                },
              },
              tooltip: {
                sx: {
                  fontSize: "0.85vw",
                  padding: "0.5vw 0.8vw",
                  borderRadius: "8px",
                  backgroundColor: "rgba(24, 35, 79, 0.9)",
                  fontWeight: "bold",
                  color: "#ffffff",
                },
              },
              arrow: {
                sx: {
                  color: "rgba(24, 35, 79, 0.9)",
                },
              },
            }}
          >
            <IconButton
              onClick={handleExportarPDF}
              sx={{
                backgroundColor: "#ffffff",
                color: "#d32f2f",
                boxShadow: 3,
                border: "1px solid #e0e0e0",
                "&:hover": { backgroundColor: "#d1d1d1" },
                width: "3.5vw",
                height: "3.5vw",
                minWidth: "48px",
                minHeight: "48px",
              }}
            >
              <Icon path={mdiFilePdfBox} size={1.5} color="#d32f2f" />
            </IconButton>
          </Tooltip>

          {/* Botón Excel */}
          <Tooltip
            title="Exportar Reporte a Excel"
            arrow
            placement="left"
            slotProps={{
              popper: {
                sx: {
                  [`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.tooltip}`]: {
                    marginRight: "0px",
                  },
                },
              },
              tooltip: {
                sx: {
                  fontSize: "0.85vw",
                  padding: "0.5vw 0.8vw",
                  borderRadius: "8px",
                  backgroundColor: "rgba(24, 35, 79, 0.9)",
                  fontWeight: "bold",
                  color: "#ffffff",
                },
              },
              arrow: {
                sx: {
                  color: "rgba(24, 35, 79, 0.9)",
                },
              },
            }}
          >
            <IconButton
              onClick={handleExportarExcel}
              sx={{
                backgroundColor: "#ffffff",
                color: "#2e7d32",
                boxShadow: 3,
                border: "1px solid #e0e0e0",
                "&:hover": { backgroundColor: "#d1d1d1" },
                width: "3.5vw",
                height: "3.5vw",
                minWidth: "48px",
                minHeight: "48px",
              }}
            >
              <Icon path={mdiFileExcel} size={1.5} color="#2e7d32" />
            </IconButton>
          </Tooltip>
        </Box>
      </Content>
    </Layout>
  );
};

export default ReporteDescuentos;
